import { SCORE_IMPACTS } from '@parcel/filters';
/**
 * The 36 model-facing descriptors, in advertised order.
 *
 * These moved here verbatim from apps/mcp so one definition serves the MCP
 * server, Spec's native dispatch, Settings, and the generated documentation.
 * Dispatch stayed behind in each transport: this file knows what a tool looks
 * like, never how to call it.
 *
 * The name/inputSchema pairs are locked by mcp-tool-contract.baseline.json,
 * captured from the live server before the move.
 */
import {
  BATCH_MAX,
  FEED_PAGE_MAX,
  FEED_PRIORITIES,
  FEED_ROW_ITEM_CAP,
  FEED_TOOL_ITEMS,
  FEED_TOOL_LIMIT,
  FIELDS_DATASET_IDS,
  INCLUDE_WHITELIST,
  SEARCH_SCHEMAS,
  SUGGESTION_PAGE_MAX,
  SUGGESTION_SKIP_DAYS,
  WATCH_DATASETS,
  WORKSPACE_SEARCH_SCHEMAS,
} from '@parcel/types';
import { buildGetInputSchema, buildSearchInputSchema } from './filter-schema.js';
import { QUERY_DATA_SQL_DESCRIPTION } from './spec-read-ddl.js';
import type { ToolDescriptor } from './types.js';
import { buildWhereSchema } from './where-schema.js';

// ---------------------------------------------------------------------------
// Global data tools (search_* / get_* for the four indexed entities)
// ---------------------------------------------------------------------------

export const DATA_ENTITIES = ['accounts', 'contacts', 'projects', 'signals'] as const;
type DataEntity = (typeof DATA_ENTITIES)[number];

const ENTITY_SINGULAR = {
  accounts: 'account',
  contacts: 'contact',
  projects: 'project',
  signals: 'signal',
} as const;

// Ranking accounts by activity is one call; without this a model counts projects one account at a time.
const RANKING_HINT: Partial<Record<string, string>> = {
  accounts: ' Rank by activity: sort projects_count desc (has_project scopes it, e.g. one city).',
  projects:
    ' Rank by activity: sort signals_count desc (has_signal scopes it, e.g. one signal type).',
  contacts: ' Rank by activity: sort projects_count desc (on_project scopes it).',
};

// Scores rank by fit; the bucket label and matched criteria ride on every record.
const SCORE_HINT: Partial<Record<string, string>> = {
  accounts: ' Rank by fit: sort icp_score desc; records carry scores.icp {score, bucket, matched}.',
  projects:
    ' Rank by fit: sort atlas_score desc; records carry scores.atlas {score, bucket, matched}.',
  contacts:
    ' Rank by fit: sort prospect_score desc; records carry scores.prospect {score, bucket, matched}.',
};

const WHERE_DATASET_OF = { accounts: 'account', projects: 'project', contacts: 'contact' } as const;

// The book's activity ranking; where narrows it (a city, a signal type) the way has_* does on the Data API.
const WS_RANKING_HINT: Partial<Record<string, string>> = {
  accounts: ' Rank by activity: sort projects_count desc.',
  projects: ' Rank by activity: sort signals_count desc.',
  contacts: ' Rank by activity: sort projects_count desc.',
};

function buildDataDescriptors(): ToolDescriptor[] {
  const result: ToolDescriptor[] = [];
  for (const entity of DATA_ENTITIES) {
    const descriptor = SEARCH_SCHEMAS[entity];
    const relHint = Object.keys(descriptor.relational ?? {})
      .slice(0, 2)
      .join(', ');
    // ENTITY_SINGULAR values are all valid INCLUDE_WHITELIST keys.
    // biome-ignore lint/style/noNonNullAssertion: keys guaranteed by ENTITY_SINGULAR
    const includeKeys = Object.keys(INCLUDE_WHITELIST[ENTITY_SINGULAR[entity]]!);
    result.push({
      name: `search_${entity}`,
      description: `Search Parcel ${entity} using filters, full-text query, and cursor pagination.${relHint ? ` Supports relational filters (e.g. ${relHint}).` : ''} Call search_fields first when unsure of valid fields or enum values.${RANKING_HINT[entity] ?? ''}`,
      inputSchema: buildSearchInputSchema(descriptor, entity) as ToolDescriptor['inputSchema'],
    });
    result.push({
      name: `get_${entity}`,
      description: `Fetch one or more Parcel ${entity} by ID. Optional include: ${includeKeys.join(', ')}.`,
      inputSchema: buildGetInputSchema(includeKeys) as ToolDescriptor['inputSchema'],
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Fields dataset tools
// ---------------------------------------------------------------------------

const FIELD_KINDS = ['enum', 'text', 'number', 'date', 'relation', 'choice', 'yes_no', 'score'];

const SEARCH_FIELDS: ToolDescriptor = {
  name: 'search_fields',
  description:
    'Discover the filterable fields, enum values with meanings, and relational sub-fields for ' +
    'any dataset before composing a search. search_fields({dataset: "projects"}) returns the ' +
    'full project vocabulary in one call. Call this first when unsure of valid fields or enum ' +
    'values; use get_fields for a targeted deep dive on known keys. 0 credits.',
  inputSchema: {
    type: 'object',
    properties: {
      dataset: {
        type: 'string',
        enum: [...FIELDS_DATASET_IDS],
        description: 'Limit to one dataset.',
      },
      kind: { type: 'string', enum: FIELD_KINDS, description: 'Limit to one field kind.' },
      query: {
        type: 'string',
        minLength: 1,
        description: 'Case-insensitive substring match over key and description.',
      },
    },
  },
};

const GET_FIELDS: ToolDescriptor = {
  name: 'get_fields',
  description:
    'Fetch one or more field descriptors by key: kind, description, every enum value with its ' +
    'meaning, and relational sub-fields. Keys that match nothing are listed in ' +
    'metadata.not_found. The targeted deep dive; use search_fields to discover a whole ' +
    'dataset. 0 credits.',
  inputSchema: {
    type: 'object',
    properties: {
      dataset: {
        type: 'string',
        enum: [...FIELDS_DATASET_IDS],
        description: 'The dataset the keys belong to.',
      },
      keys: {
        type: 'array',
        items: { type: 'string', minLength: 1 },
        minItems: 1,
        description: 'Field keys to fetch (e.g. ["stage", "project.city"]).',
      },
    },
    required: ['dataset', 'keys'],
  },
};

// ---------------------------------------------------------------------------
// Workspace book reads
// ---------------------------------------------------------------------------

const WORKSPACE_READ_ENTITIES = Object.keys(WORKSPACE_SEARCH_SCHEMAS) as Array<
  keyof typeof WORKSPACE_SEARCH_SCHEMAS
>;

type WorkspaceReadEntity = (typeof WORKSPACE_READ_ENTITIES)[number];

// All workspace overlay entities are regular plurals, so the singular (used to
// name the get tool and as the INCLUDE_WHITELIST key) is the plural minus its
// trailing 's'.
export function singularOf(entity: string): string {
  return entity.replace(/s$/, '');
}

function buildWorkspaceSearchInputSchema(
  descriptor: (typeof WORKSPACE_SEARCH_SCHEMAS)[WorkspaceReadEntity],
  entity: WorkspaceReadEntity,
): ToolDescriptor['inputSchema'] {
  return {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        minLength: 1,
        description: descriptor.queryDescription ?? 'Full-text search query.',
      },
      where: buildWhereSchema(WHERE_DATASET_OF[entity]),
      sort: {
        type: 'object',
        additionalProperties: false,
        description: 'Sort field + order (default asc).',
        properties: {
          field: { enum: [...descriptor.sortKeys] },
          order: { enum: ['asc', 'desc'], default: 'asc' },
        },
        required: ['field'],
      },
      limit: {
        type: 'number',
        minimum: 1,
        maximum: 100,
        default: 25,
        description: 'Max results (1-100).',
      },
      offset: {
        type: 'number',
        minimum: 0,
        maximum: 10000,
        default: 0,
        description: 'Results to skip.',
      },
      search_after: { type: 'string', description: 'Cursor past offset.' },
      view_id: {
        type: 'string',
        format: 'uuid',
        description:
          'Optional shared View id (workspace_search_views). Its saved condition or membership is the base scope; this call’s where only narrows it. With view_id, the updated_at and score sorts are book-only.',
      },
    },
  };
}

function buildWorkspaceGetInputSchema(includeKeys: string[]): ToolDescriptor['inputSchema'] {
  return {
    type: 'object',
    properties: {
      id: { type: 'string', description: 'The record id to fetch from your workspace book.' },
      include: {
        type: 'array',
        items: { enum: [...includeKeys] },
        description: `Optional related resources to include: ${includeKeys.join(', ')}.`,
      },
    },
    required: ['id'],
  };
}

function buildWorkspaceReadDescriptors(): ToolDescriptor[] {
  const result: ToolDescriptor[] = [];
  for (const entity of WORKSPACE_READ_ENTITIES) {
    const descriptor = WORKSPACE_SEARCH_SCHEMAS[entity];
    const singular = singularOf(entity);
    const includeSpec = INCLUDE_WHITELIST[singular];
    if (!includeSpec) {
      throw new Error(
        `INCLUDE_WHITELIST has no entry for singular "${singular}" (derived from "${entity}"). Update singularOf() or add the INCLUDE_WHITELIST entry.`,
      );
    }
    const includeKeys = Object.keys(includeSpec);
    result.push({
      name: `workspace_search_${entity}`,
      description: `Search ${entity} in your workspace book (saved/annotated only). Filter with where; search_fields({dataset:"workspace_${entity}"}) lists fields, operators and values. Optional view_id scopes to a View.${WS_RANKING_HINT[entity] ?? ''}${SCORE_HINT[entity] ?? ''} Requires the Pro plan.`,
      inputSchema: buildWorkspaceSearchInputSchema(descriptor, entity),
    });
    result.push({
      name: `workspace_get_${singular}`,
      description:
        `Fetch one ${singular} from your workspace book by id, with overlay fields, its score, and ` +
        `optional include: ${includeKeys.join(', ')}. Requires the Pro plan.`,
      inputSchema: buildWorkspaceGetInputSchema(includeKeys),
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

const VIEW_DATASETS = ['account', 'contact', 'project'] as const;
const VIEW_TYPES = ['dynamic', 'static'] as const;

// `favorite` covers repositioning too: the batch endpoint behind it upserts each
// {view_id, position} row, so there is no separate reorder action.
export const MANAGE_VIEW_ACTIONS = [
  'create',
  'update',
  'delete',
  'add_records',
  'remove_records',
  'favorite',
  'unfavorite',
  'watch',
] as const;

const dynamicDefinition = (dataset: (typeof VIEW_DATASETS)[number]) => ({
  type: 'object',
  additionalProperties: false,
  required: ['version', 'q', 'where'],
  properties: {
    version: { const: 2 },
    q: { type: 'string', description: 'Search chip text; empty for none.' },
    where: buildWhereSchema(dataset),
  },
});

const staticDefinition = {
  type: 'object',
  additionalProperties: false,
  required: ['version'],
  properties: { version: { const: 1 } },
};

// anyOf, not oneOf: the dataset branches overlap (city is in all three), and `dataset` picks the branch.
const viewDefinition = {
  anyOf: [...VIEW_DATASETS.map(dynamicDefinition), staticDefinition],
  description: 'Dynamic {version:2,q,where} for the dataset, or Static {version:1}.',
};

const SEARCH_VIEWS: ToolDescriptor = {
  name: 'workspace_search_views',
  description:
    'List or look up shared workspace Views. Filter by id, name query, dataset, view_type, or ' +
    'favorite_only. Use the View id with workspace_search_accounts/contacts/projects via view_id. ' +
    'Requires the Pro plan. 0 credits.',
  inputSchema: {
    type: 'object',
    properties: {
      id: { type: 'string', format: 'uuid', description: 'Fetch one View by id.' },
      query: { type: 'string', minLength: 1, description: 'Case-insensitive name substring.' },
      dataset: { type: 'string', enum: [...VIEW_DATASETS], description: 'Limit to one dataset.' },
      view_type: {
        type: 'string',
        enum: [...VIEW_TYPES],
        description: 'Limit to Dynamic or Static.',
      },
      favorite_only: {
        type: 'boolean',
        description: 'Only Favorites for the current user (requires user identity).',
      },
      limit: {
        type: 'number',
        minimum: 1,
        maximum: 100,
        default: 25,
        description: 'Max results (1 to 100).',
      },
      offset: {
        type: 'number',
        minimum: 0,
        default: 0,
        description: 'Number of results to skip.',
      },
    },
  },
};

const MANAGE_VIEWS: ToolDescriptor = {
  name: 'workspace_manage_views',
  description:
    'Mutate Views via action (create/update/delete/add_records/remove_records/favorite/' +
    'unfavorite/watch). Prefer one batch call (up to 100). Static create may seed ' +
    'initial_record_ids (single only). favorite also sets order: send every Favorite in one ' +
    'batch with positions. Dynamic definitions are {version:2,q,where}, the same where the ' +
    'workspace searches take; a created View shows as a tab on its table. Requires Pro. 0 credits.',
  inputSchema: {
    type: 'object',
    properties: {
      action: {
        type: 'string',
        enum: [...MANAGE_VIEW_ACTIONS],
        description: 'Mutation to perform.',
      },
      name: { type: 'string', minLength: 1, description: 'View name.' },
      dataset: { type: 'string', enum: [...VIEW_DATASETS], description: 'Immutable dataset.' },
      view_type: { type: 'string', enum: [...VIEW_TYPES], description: 'Immutable type.' },
      definition: viewDefinition,
      presentation: {
        type: 'object',
        description: 'Client table presentation; ignored by View-record search.',
      },
      initial_record_ids: {
        type: 'array',
        items: { type: 'string', format: 'uuid' },
        minItems: 1,
        maxItems: BATCH_MAX,
        description: 'Static single-create seed (1-100). Omitted from batch create.',
      },
      id: { type: 'string', format: 'uuid', description: 'View id (update/delete).' },
      expected_revision: {
        type: 'number',
        minimum: 1,
        description: 'Optimistic concurrency revision.',
      },
      ids: {
        type: 'array',
        items: { type: 'string', format: 'uuid' },
        minItems: 1,
        maxItems: BATCH_MAX,
        description: 'Batch delete View ids. Use INSTEAD of id.',
      },
      view_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target View for membership/Favorite.',
      },
      view_ids: {
        type: 'array',
        items: { type: 'string', format: 'uuid' },
        minItems: 1,
        maxItems: BATCH_MAX,
        description: 'Batch unfavorite. Use INSTEAD of view_id.',
      },
      record_id: { type: 'string', format: 'uuid', description: 'Single membership record id.' },
      record_ids: {
        type: 'array',
        items: { type: 'string', format: 'uuid' },
        minItems: 1,
        maxItems: BATCH_MAX,
        description: 'Batch membership ids. Use INSTEAD of record_id.',
      },
      position: { type: 'number', minimum: 0, description: 'Favorite position (0-based).' },
      watched: {
        type: 'boolean',
        description:
          'watch: true feeds a Static View to the News feed; Following Views stay watched.',
      },
      inputs: {
        type: 'array',
        minItems: 1,
        maxItems: BATCH_MAX,
        items: { type: 'object' },
        description:
          'Batch create/update View bodies, favorite {view_id, position} rows, or watch ' +
          '{id, watched} rows.',
      },
    },
    required: ['action'],
    oneOf: [
      {
        properties: { action: { const: 'create' } },
        oneOf: [
          { required: ['name', 'dataset', 'view_type', 'definition'] },
          { required: ['inputs'] },
        ],
      },
      {
        properties: { action: { const: 'update' } },
        oneOf: [{ required: ['id', 'expected_revision'] }, { required: ['inputs'] }],
      },
      {
        properties: { action: { const: 'delete' } },
        oneOf: [{ required: ['id'] }, { required: ['ids'] }],
      },
      {
        properties: { action: { const: 'add_records' } },
        required: ['view_id'],
        oneOf: [{ required: ['record_id'] }, { required: ['record_ids'] }],
      },
      {
        properties: { action: { const: 'remove_records' } },
        required: ['view_id'],
        oneOf: [{ required: ['record_id'] }, { required: ['record_ids'] }],
      },
      {
        properties: { action: { const: 'favorite' } },
        oneOf: [{ required: ['view_id'] }, { required: ['inputs'] }],
      },
      {
        properties: { action: { const: 'unfavorite' } },
        oneOf: [{ required: ['view_id'] }, { required: ['view_ids'] }],
      },
      {
        properties: { action: { const: 'watch' } },
        oneOf: [{ required: ['id', 'watched'] }, { required: ['inputs'] }],
      },
    ],
  },
};

export const FOLLOW_ACTIONS = ['follow', 'unfollow', 'skip', 'unskip'] as const;

const FOLLOW: ToolDescriptor = {
  name: 'workspace_follow',
  description: `Follow or unfollow accounts and projects. Following adds the record to the workspace Following View for its dataset (watched: the News feed and notifications cover it); unfollow leaves other watched Views alone. skip hides a Suggested record for ${SUGGESTION_SKIP_DAYS} days (or until its bucket rises); unskip brings it back. One record_id, or record_ids (up to 100). Requires Pro. 0 credits.`,
  inputSchema: {
    type: 'object',
    properties: {
      action: { type: 'string', enum: [...FOLLOW_ACTIONS] },
      dataset: { type: 'string', enum: ['project', 'account'], description: 'Record kind.' },
      record_id: { type: 'string', format: 'uuid', description: 'One record id.' },
      record_ids: {
        type: 'array',
        items: { type: 'string', format: 'uuid' },
        minItems: 1,
        maxItems: BATCH_MAX,
        description: 'Many record ids. Use INSTEAD of record_id.',
      },
    },
    required: ['action', 'dataset'],
    oneOf: [{ required: ['record_id'] }, { required: ['record_ids'] }],
  },
};

const FEED: ToolDescriptor = {
  name: 'workspace_feed',
  description: `What changed on followed accounts and projects: one row per record, newest first, with its signals and documents from the last 30 days. suggested: true lists the best-scored records not followed yet (paged with offset, up to ${SUGGESTION_PAGE_MAX}). Read-only. Requires Pro. 0 credits.`,
  inputSchema: {
    type: 'object',
    properties: {
      dataset: { type: 'string', enum: [...WATCH_DATASETS] },
      suggested: { type: 'boolean', description: 'Suggested to follow instead of the News feed.' },
      offset: { type: 'integer', minimum: 0, description: 'Suggested only: next_offset.' },
      view_id: { type: 'string', format: 'uuid', description: 'One watched View.' },
      unseen: { type: 'boolean' },
      priority: { type: 'string', enum: [...FEED_PRIORITIES] },
      cursor: { type: 'string', description: 'next_cursor of the last page.' },
      limit: {
        type: 'integer',
        minimum: 1,
        maximum: FEED_PAGE_MAX,
        description: `Rows to return (default ${FEED_TOOL_LIMIT}).`,
      },
      items: {
        type: 'integer',
        minimum: 1,
        maximum: FEED_ROW_ITEM_CAP,
        description: `Items per row (default ${FEED_TOOL_ITEMS}); item_count counts them all.`,
      },
    },
    additionalProperties: false,
  },
};

/** The feed tool's arguments with its default page and row sizes filled in. */
export function feedToolArgs(args: Record<string, unknown>): Record<string, unknown> {
  return { ...args, limit: args.limit ?? FEED_TOOL_LIMIT, items: args.items ?? FEED_TOOL_ITEMS };
}

/** Where a workspace_feed call reads: the Feed, or with suggested the Suggested list, each with its own arguments. */
export function feedToolRequest(args: Record<string, unknown>): {
  path: '/workspace/feed' | '/workspace/feed/suggested';
  args: Record<string, unknown>;
} {
  const { suggested, offset, ...feed } = args;
  if (suggested !== true) return { path: '/workspace/feed', args: feedToolArgs(feed) };
  const limit = typeof args.limit === 'number' ? args.limit : FEED_TOOL_LIMIT;
  return {
    path: '/workspace/feed/suggested',
    args: {
      ...(args.dataset !== undefined ? { dataset: args.dataset } : {}),
      ...(offset !== undefined ? { offset } : {}),
      limit: Math.min(limit, SUGGESTION_PAGE_MAX),
    },
  };
}

// ---------------------------------------------------------------------------
// Knowledge (facts about the firm: pages in folders, sections, attached files)
// ---------------------------------------------------------------------------

const FIND_KNOWLEDGE: ToolDescriptor = {
  name: 'find_knowledge',
  description:
    'Search the workspace knowledge library (facts about this firm: who they are, services, ICP, ' +
    'personas, positioning, playbooks). Send 1-4 phrasings; returns matching sections with page ' +
    'ref, heading and snippet. With no queries, returns the page tree. 0 credits.',
  inputSchema: {
    type: 'object',
    properties: {
      queries: {
        type: 'array',
        items: { type: 'string', minLength: 1, maxLength: 200 },
        minItems: 1,
        maxItems: 4,
        description: 'Phrasings of what to find.',
      },
      limit: {
        type: 'integer',
        minimum: 1,
        maximum: 20,
        description: 'Sections to return (default 8).',
      },
      path: { type: 'string', maxLength: 300, description: 'Only this folder and below.' },
    },
    additionalProperties: false,
  },
};

const GET_KNOWLEDGE: ToolDescriptor = {
  name: 'get_knowledge',
  description:
    'Read a knowledge page (by id or "folder/Title" ref), one section (its id from find_knowledge is enough), or an attached ' +
    "file's text. Returns 12,000 characters per call; while next_offset is not null more remains, so pass it as offset to read on before summarizing the whole. 0 credits.",
  inputSchema: {
    type: 'object',
    properties: {
      page: {
        type: 'string',
        minLength: 1,
        maxLength: 600,
        description: 'Page id or "folder/Title".',
      },
      section: {
        type: 'string',
        format: 'uuid',
        description: 'Section id from find_knowledge; enough on its own.',
      },
      file: {
        type: 'string',
        format: 'uuid',
        description: 'Attached file id; use instead of page.',
      },
      offset: { type: 'integer', minimum: 0, description: 'Character offset (next_offset).' },
    },
    additionalProperties: false,
  },
};

export const MANAGE_KNOWLEDGE_COMMANDS = [
  'create',
  'str_replace',
  'insert',
  'replace',
  'move',
  'delete',
  'restore',
] as const;

const KNOWLEDGE_MANAGE_PROPERTIES = {
  command: { type: 'string', enum: [...MANAGE_KNOWLEDGE_COMMANDS], description: 'What to do.' },
  page: { type: 'string', minLength: 1, maxLength: 600, description: 'Page id or "folder/Title".' },
  expected_revision: {
    type: 'integer',
    minimum: 1,
    description: "The page's current revision (every command but create and restore).",
  },
  path: { type: 'string', maxLength: 600, description: 'Folder, e.g. "Go-to-market".' },
  title: { type: 'string', minLength: 1, maxLength: 200, description: 'Page title.' },
  description: {
    type: ['string', 'null'],
    maxLength: 300,
    description: 'One line. Omit unless you are changing it; null clears it.',
  },
  body: { type: 'string', description: 'Markdown (create, replace).' },
  old_text: {
    type: 'string',
    minLength: 1,
    description: 'Exact text to replace, once on the page.',
  },
  new_text: { type: 'string', description: 'Replacement text.' },
  text: { type: 'string', minLength: 1, description: 'Markdown to insert.' },
  after_heading: {
    type: 'string',
    minLength: 1,
    description: 'Insert after this heading (or "A › B" trail); default end of page.',
  },
  from_file: {
    type: 'string',
    format: 'uuid',
    description: 'Spec chat only: save an attached chat file into knowledge (create).',
  },
};

const MANAGE_KNOWLEDGE: ToolDescriptor = {
  name: 'manage_knowledge',
  description:
    'Write workspace knowledge: create a page (markdown body), edit by exact str_replace, insert ' +
    'after a heading, replace, move to a folder, delete (archive) or restore. Edits need the ' +
    "page's current revision. Leave description out unless you mean to change it. Prefer one " +
    'batch call (up to 100). 0 credits.',
  inputSchema: {
    type: 'object',
    properties: {
      ...KNOWLEDGE_MANAGE_PROPERTIES,
      inputs: {
        type: 'array',
        minItems: 1,
        maxItems: BATCH_MAX,
        items: { type: 'object', properties: KNOWLEDGE_MANAGE_PROPERTIES, required: ['command'] },
        description:
          'Batch: 1-100 commands, each with its own fields. Use INSTEAD of the top level.',
      },
    },
    oneOf: [
      { required: ['inputs'] },
      {
        required: ['command'],
        oneOf: [
          { properties: { command: { const: 'create' } }, required: ['title'] },
          {
            properties: { command: { const: 'str_replace' } },
            required: ['page', 'expected_revision', 'old_text', 'new_text'],
          },
          {
            properties: { command: { const: 'insert' } },
            required: ['page', 'expected_revision', 'text'],
          },
          {
            properties: { command: { const: 'replace' } },
            required: ['page', 'expected_revision'],
          },
          {
            properties: { command: { const: 'move' } },
            required: ['page', 'expected_revision', 'path'],
          },
          {
            properties: { command: { const: 'delete' } },
            required: ['page', 'expected_revision'],
          },
          { properties: { command: { const: 'restore' } }, required: ['page'] },
        ],
      },
    ],
  },
};

// ---------------------------------------------------------------------------
// Workspace writes, metadata reads, and data feedback
// ---------------------------------------------------------------------------

// Wrap a single-record write schema so the tool also accepts a batch array.
// oneOf enforces "single fields XOR the batch array": a call providing both, or
// neither, fails validation cleanly.
function withBatchInputs(opts: {
  properties: Record<string, unknown>;
  // The single-record constraint: {anyOf: [...]} or {required: [...]}.
  singleConstraint: Record<string, unknown>;
  itemSchema: Record<string, unknown>;
  batchKey: 'inputs' | 'ids' | 'urls';
  batchDescription: string;
}): ToolDescriptor['inputSchema'] {
  return {
    type: 'object',
    properties: {
      ...opts.properties,
      [opts.batchKey]: {
        type: 'array',
        items: opts.itemSchema,
        minItems: 1,
        maxItems: BATCH_MAX,
        description: opts.batchDescription,
      },
    },
    oneOf: [{ required: [opts.batchKey] }, opts.singleConstraint],
  };
}

export const ACCOUNT_UPSERT_PROPERTIES = {
  account_id: { type: 'string', description: 'The global account id. Provide this or match.' },
  match: {
    type: 'object',
    additionalProperties: false,
    description: 'Resolve the account by natural key instead of id.',
    properties: {
      domain: { type: 'string', minLength: 1, description: 'A domain of the account.' },
    },
    required: ['domain'],
  },
  workspace_status: {
    description: 'Free-text workflow status, or null to clear.',
    oneOf: [{ type: 'string' }, { type: 'null' }],
  },
  notes: {
    description: 'Free-text notes, or null to clear.',
    oneOf: [{ type: 'string' }, { type: 'null' }],
  },
  tags: {
    type: 'array',
    items: { type: 'string' },
    description: 'Full set of tags (replaces existing).',
  },
};

// Require at least one identifier. The API accepts both (account_id wins), so
// this is anyOf (at least one), not oneOf (exactly one).
const ACCOUNT_IDENTIFIER_CONSTRAINT = {
  anyOf: [{ required: ['account_id'] }, { required: ['match'] }],
};

const UPSERT_ACCOUNT: ToolDescriptor = {
  name: 'workspace_upsert_account',
  description:
    'Add or update an account in your workspace book. Identify it by account_id or by ' +
    'match:{domain} (resolved against the global account; 0 matches errors 404, more than 1 ' +
    'errors 409). Sets your overlay fields (workspace_status, notes, tags). Omitted ' +
    'fields are unchanged; an explicit null clears a field; tags is full-replace. ' +
    'Updating more than one account? Pass them all in ' +
    'inputs (an array of up to 100 of these records) in a SINGLE call instead of calling this ' +
    'tool repeatedly; results return per item, in input order, and one bad record never blocks ' +
    'the rest. Requires the Pro plan. 0 credits.',
  inputSchema: withBatchInputs({
    properties: ACCOUNT_UPSERT_PROPERTIES,
    singleConstraint: ACCOUNT_IDENTIFIER_CONSTRAINT,
    itemSchema: {
      type: 'object',
      properties: ACCOUNT_UPSERT_PROPERTIES,
      ...ACCOUNT_IDENTIFIER_CONSTRAINT,
    },
    batchKey: 'inputs',
    batchDescription:
      'Batch mode: an array of 1-100 single-record bodies. Use INSTEAD of the top-level fields.',
  }),
};

const DELETE_ACCOUNT: ToolDescriptor = {
  name: 'workspace_delete_account',
  description:
    'Remove an account from your workspace book by id. Your overlay record is deleted; the ' +
    'global account is untouched. Idempotent. Removing more than one? Pass ids (an array of up ' +
    'to 100) in a SINGLE call instead of calling this tool repeatedly. Requires the Pro plan. ' +
    '0 credits.',
  inputSchema: withBatchInputs({
    properties: {
      id: { type: 'string', description: 'The account id to remove from your book.' },
    },
    singleConstraint: { required: ['id'] },
    itemSchema: { type: 'string', format: 'uuid' },
    batchKey: 'ids',
    batchDescription: 'Batch mode: an array of 1-100 account ids. Use INSTEAD of the top-level id.',
  }),
};

export const PROJECT_UPSERT_PROPERTIES = {
  project_id: { type: 'string', description: 'The global project id.' },
  workspace_status: {
    description: 'Free-text workflow status, or null to clear.',
    oneOf: [{ type: 'string' }, { type: 'null' }],
  },
  notes: {
    description: 'Free-text notes, or null to clear.',
    oneOf: [{ type: 'string' }, { type: 'null' }],
  },
  tags: {
    type: 'array',
    items: { type: 'string' },
    description: 'Full set of tags (replaces existing).',
  },
};

const UPSERT_PROJECT: ToolDescriptor = {
  name: 'workspace_upsert_project',
  description:
    'Create or update your overlay (workspace_status, notes, tags) for a project in your workspace book. ' +
    'Projects are id-only (pass project_id). 0 credits. Requires the Pro plan. ' +
    'Updating more than one project? Pass them all in inputs (up to 100) in a SINGLE call; ' +
    'results return per item in input order.',
  inputSchema: withBatchInputs({
    properties: PROJECT_UPSERT_PROPERTIES,
    singleConstraint: { required: ['project_id'] },
    itemSchema: {
      type: 'object',
      properties: PROJECT_UPSERT_PROPERTIES,
      required: ['project_id'],
    },
    batchKey: 'inputs',
    batchDescription:
      'Batch mode: an array of 1-100 single-record bodies. Use INSTEAD of the top-level fields.',
  }),
};

const DELETE_PROJECT: ToolDescriptor = {
  name: 'workspace_delete_project',
  description:
    'Remove a project from your workspace book. The global project is untouched. Idempotent. ' +
    'Removing more than one? Pass ids (an array of up to 100) in a SINGLE call instead of ' +
    'calling this tool repeatedly. Requires the Pro plan. 0 credits.',
  inputSchema: withBatchInputs({
    properties: {
      id: { type: 'string', description: 'The project id to remove from your book.' },
    },
    singleConstraint: { required: ['id'] },
    itemSchema: { type: 'string', format: 'uuid' },
    batchKey: 'ids',
    batchDescription: 'Batch mode: an array of 1-100 project ids. Use INSTEAD of the top-level id.',
  }),
};

export const CONTACT_UPSERT_PROPERTIES = {
  contact_id: { type: 'string', description: 'The global contact id. Provide this or match.' },
  match: {
    type: 'object',
    additionalProperties: false,
    description: 'Resolve the contact by natural key instead of id.',
    properties: {
      email: { type: 'string', minLength: 1, description: 'Email address of the contact.' },
    },
    required: ['email'],
  },
  workspace_status: {
    description: 'Free-text workflow status, or null to clear.',
    oneOf: [{ type: 'string' }, { type: 'null' }],
  },
  notes: {
    description: 'Free-text notes, or null to clear.',
    oneOf: [{ type: 'string' }, { type: 'null' }],
  },
  tags: {
    type: 'array',
    items: { type: 'string' },
    description: 'Full set of tags (replaces existing).',
  },
};

const CONTACT_IDENTIFIER_CONSTRAINT = {
  anyOf: [{ required: ['contact_id'] }, { required: ['match'] }],
};

const UPSERT_CONTACT: ToolDescriptor = {
  name: 'workspace_upsert_contact',
  description:
    'Create or update your overlay (workspace_status, notes, tags) for a contact in your workspace book, ' +
    'identified by contact_id or match:{email}. 0 credits. Requires the Pro plan. ' +
    'Updating more than one contact? Pass them all in inputs (up to 100) in a SINGLE call; ' +
    'results return per item in input order.',
  inputSchema: withBatchInputs({
    properties: CONTACT_UPSERT_PROPERTIES,
    singleConstraint: CONTACT_IDENTIFIER_CONSTRAINT,
    itemSchema: {
      type: 'object',
      properties: CONTACT_UPSERT_PROPERTIES,
      ...CONTACT_IDENTIFIER_CONSTRAINT,
    },
    batchKey: 'inputs',
    batchDescription:
      'Batch mode: an array of 1-100 single-record bodies. Use INSTEAD of the top-level fields.',
  }),
};

const DELETE_CONTACT: ToolDescriptor = {
  name: 'workspace_delete_contact',
  description:
    'Remove a contact from your workspace book. The global contact is untouched. Idempotent. ' +
    'Removing more than one? Pass ids (an array of up to 100) in a SINGLE call instead of ' +
    'calling this tool repeatedly. Requires the Pro plan. 0 credits.',
  inputSchema: withBatchInputs({
    properties: {
      id: { type: 'string', description: 'The contact id to remove from your book.' },
    },
    singleConstraint: { required: ['id'] },
    itemSchema: { type: 'string', format: 'uuid' },
    batchKey: 'ids',
    batchDescription: 'Batch mode: an array of 1-100 contact ids. Use INSTEAD of the top-level id.',
  }),
};

const LIST_SUBSCRIPTIONS: ToolDescriptor = {
  name: 'workspace_list_subscriptions',
  description:
    "List this workspace's subscribed signal types, each with its News feed priority. " +
    'Pro plan required. 0 credits.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      workspace_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target workspace (defaults to the API key workspace).',
      },
    },
    required: [],
  },
};

const UPSERT_SUBSCRIPTION: ToolDescriptor = {
  name: 'workspace_upsert_subscription',
  description:
    'Subscribe to a signal type or set its priority. Idempotent: keyed by signal_type. A subscribed ' +
    'type counts in the News feed on followed accounts and projects, at its priority. Email is personal: ' +
    'each member sets it in the app. Pro plan required. 0 credits.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      workspace_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target workspace (defaults to the API key workspace).',
      },
      signal_type: {
        type: 'string',
        description: 'The signal type to subscribe to (e.g. permit_filed, public_review).',
      },
      priority: {
        type: 'string',
        enum: ['high', 'medium', 'low'],
        description: 'The News feed priority for signals of this type.',
      },
    },
    required: ['signal_type'],
  },
};

const DELETE_SUBSCRIPTION: ToolDescriptor = {
  name: 'workspace_delete_subscription',
  description:
    'Remove a signal subscription by its row id. Signals of this type leave the News feed and its ' +
    'emails immediately. Idempotent: deleting an absent id returns success. ' +
    'Pro plan required. 0 credits.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      workspace_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target workspace (defaults to the API key workspace).',
      },
      id: {
        type: 'string',
        format: 'uuid',
        description: 'The subscription row id (from workspace_list_subscriptions).',
      },
    },
    required: ['id'],
  },
};

const LIST_MEMBERS: ToolDescriptor = {
  name: 'workspace_list_members',
  description:
    'List all members of the workspace with their id, email, display name, role, and join date. Pro plan required. 0 credits.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      workspace_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target workspace (defaults to the API key workspace).',
      },
    },
    required: [],
  },
};

const UPDATE_MEMBER: ToolDescriptor = {
  name: 'workspace_update_member',
  description:
    'Change the role of a workspace member to owner, admin, or member. Owners and admins only. Cannot demote the last owner (409). Pro plan required. 0 credits.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      workspace_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target workspace (defaults to the API key workspace).',
      },
      userId: { type: 'string', description: 'The user id of the member to update.' },
      role: {
        type: 'string',
        enum: ['owner', 'admin', 'member'],
        description: 'New role.',
      },
    },
    required: ['userId', 'role'],
  },
};

const DELETE_MEMBER: ToolDescriptor = {
  name: 'workspace_delete_member',
  description:
    'Remove a user from the workspace. Owners and admins only. Cannot remove the last owner (409). Idempotent: a missing member returns deleted=false. Pro plan required. 0 credits.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      workspace_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target workspace (defaults to the API key workspace).',
      },
      userId: { type: 'string', description: 'The user id of the member to remove.' },
    },
    required: ['userId'],
  },
};

const CREATE_INVITATION: ToolDescriptor = {
  name: 'workspace_create_invitation',
  description:
    'Invite a single user to the workspace by email. Owners and admins only. Blocks at the plan seat limit (403 seat_limit_reached). Returns 409 if a live pending invite already exists. Pro plan required. 0 credits.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      workspace_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target workspace (defaults to the API key workspace).',
      },
      email: { type: 'string', description: 'Email address of the invitee.' },
      role: {
        type: 'string',
        enum: ['admin', 'member'],
        description: 'Role to assign on acceptance: admin or member (defaults to member).',
      },
    },
    required: ['email'],
  },
};

const LIST_INVITATIONS: ToolDescriptor = {
  name: 'workspace_list_invitations',
  description:
    'List pending (not yet accepted or revoked) workspace invitations, with status (Pending, Expired). Pro plan required. 0 credits.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      workspace_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target workspace (defaults to the API key workspace).',
      },
    },
    required: [],
  },
};

const DELETE_INVITATION: ToolDescriptor = {
  name: 'workspace_delete_invitation',
  description:
    'Revoke a pending workspace invitation by id. Owners and admins only. Returns 404 if the invitation is not found or was already accepted. Pro plan required. 0 credits.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      workspace_id: {
        type: 'string',
        format: 'uuid',
        description: 'Target workspace (defaults to the API key workspace).',
      },
      id: {
        type: 'string',
        format: 'uuid',
        description: 'The invitation id to revoke.',
      },
    },
    required: ['id'],
  },
};

const GET_INFO: ToolDescriptor = {
  name: 'workspace_get_info',
  description:
    'Return the calling workspace plan, full entitlement set, seat usage ' +
    '(members + pending invites vs limit), and workspace settings. ' +
    '0-credit read; never throttled even at the credit ceiling. Requires the Pro plan.',
  inputSchema: {
    type: 'object' as const,
    properties: {},
  },
};

const GET_CREDITS: ToolDescriptor = {
  name: 'workspace_get_credits',
  description:
    'Return the 24-hour rolling credit residual for the calling workspace: ' +
    'remaining = ceiling - used. 0-credit read; never throttled, so a workspace ' +
    'can always check its own usage. Requires the Pro plan.',
  inputSchema: {
    type: 'object' as const,
    properties: {},
  },
};

const SUBMIT_DATA_FEEDBACK: ToolDescriptor = {
  name: 'submit_data_feedback',
  description:
    'Report a data issue about a project, account, contact, or signal in the Parcel index. ' +
    'Provide the entity (target_type + target_id) and a plain-language description of what looks ' +
    'wrong, missing, or stale. The report is verified and triaged before any change is made. ' +
    'Requires the Pro plan. 0 credits.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      target_type: {
        type: 'string',
        enum: ['project', 'account', 'contact', 'signal'],
        description: 'The kind of entity the issue is about.',
      },
      target_id: {
        type: 'string',
        format: 'uuid',
        description: 'The id of the project/account/contact/signal the issue is about.',
      },
      body: {
        type: 'string',
        description: 'A plain-language description of the data issue you spotted.',
      },
    },
    required: ['body'],
  },
};

// Spec only: the view DDL lives in the sql argument, which Settings never shows.
const QUERY_DATA: ToolDescriptor = {
  name: 'query_data',
  description:
    "Query Parcel's projects, accounts, contacts and signals with one read-only SQL SELECT, " +
    'scoped to this workspace. The first call for any Parcel data question. Up to 100 rows a ' +
    "call; rows count toward the workspace's daily query_data allowance.",
  inputSchema: {
    type: 'object' as const,
    properties: {
      sql: { type: 'string', description: QUERY_DATA_SQL_DESCRIPTION },
    },
    required: ['sql'],
    additionalProperties: false,
  },
};

// Spec only: Parallel through the AI Gateway, billed per call to the member's credits.
const WEB_SEARCH: ToolDescriptor = {
  name: 'web_search',
  description:
    'Search the open web, or read one page a search returned or the member gave you. Parcel is the system of ' +
    'record: query Parcel first, then use the web to verify a fact before presenting it, to fill a field Parcel ' +
    "shows as unknown, or for anything newer than a project's last_signal_at. Several web_search calls in one " +
    'step run at the same time: read the pages you need together. Billed per call and per page.',
  inputSchema: {
    type: 'object' as const,
    properties: {
      queries: {
        type: 'array',
        items: { type: 'string' },
        maxItems: 4,
        description:
          'Up to 4 searches that run together as one call and one charge. Put every angle in one call: the address, the permit or case number, the developer.',
      },
      objective: {
        type: 'string',
        description: 'Optional, with queries: what you want to learn, so results rank for it.',
      },
      url: {
        type: 'string',
        description:
          'Read this page instead of searching. Only a URL a search returned this turn or the member gave.',
      },
    },
    additionalProperties: false,
  },
};

// ---------------------------------------------------------------------------
// Enrichment (Enrich PR5): charged from the workspace's enrichment credits
// ---------------------------------------------------------------------------

/** People-search vocabularies; apps/api pins both to @parcel/core's ROLE_FAMILIES and SENIORITIES. */
export const ENRICH_ROLE_FAMILIES = [
  'development_lead',
  'project_executive',
  'development_manager',
  'business_development',
  'principal',
  'project_architect',
  'project_manager',
  'preconstruction',
] as const;
export const ENRICH_SENIORITIES = [
  'executive',
  'vp',
  'director',
  'manager',
  'individual',
  'junior',
] as const;

const SEARCH_PEOPLE: ToolDescriptor = {
  name: 'search_people',
  description:
    "Find people at up to 10 firms by role, from Parcel's people index and its data provider. Name each firm by " +
    'Parcel account id, domain, name or LinkedIn company URL, and the roles by family or title. Returns names, ' +
    'titles, LinkedIn URLs and Parcel contact ids, never emails or phones (use reveal_contact). Charged per person ' +
    "returned that Parcel did not already list at the firm, from the workspace's enrichment credits, cached " +
    'results included; people already listed are free, and nothing is charged when no one new is found.',
  inputSchema: {
    type: 'object',
    properties: {
      firms: {
        type: 'array',
        minItems: 1,
        maxItems: 10,
        description: 'The firms to search.',
        items: {
          type: 'object',
          additionalProperties: false,
          properties: {
            account_id: { type: 'string', format: 'uuid', description: 'Parcel account id.' },
            domain: {
              type: 'string',
              minLength: 1,
              description: 'Firm web domain, for example devco.com.',
            },
            name: {
              type: 'string',
              minLength: 1,
              description: 'Firm name, when no id or domain is known.',
            },
            linkedin_url: {
              type: 'string',
              minLength: 1,
              description: "The firm's LinkedIn company URL.",
            },
          },
          anyOf: [
            { required: ['account_id'] },
            { required: ['domain'] },
            { required: ['name'] },
            { required: ['linkedin_url'] },
          ],
        },
      },
      role_families: {
        type: 'array',
        minItems: 1,
        items: { type: 'string', enum: [...ENRICH_ROLE_FAMILIES] },
        description:
          "Role families; Parcel expands each into its title variants. Default: the firm type's personas.",
      },
      titles: {
        type: 'array',
        minItems: 1,
        maxItems: 20,
        items: { type: 'string', minLength: 1 },
        description: 'Raw job titles to match as well.',
      },
      seniority: {
        type: 'array',
        minItems: 1,
        items: { type: 'string', enum: [...ENRICH_SENIORITIES] },
        description: 'Keep only these seniorities.',
      },
      country: {
        type: 'string',
        minLength: 2,
        maxLength: 2,
        description: 'Two-letter country code; default US.',
      },
      city: { type: 'string', minLength: 1, description: 'A city to rank first.' },
      include_junior: {
        type: 'boolean',
        description: 'Keep assistants, coordinators and interns (dropped by default).',
      },
      limit: {
        type: 'integer',
        minimum: 1,
        maximum: 100,
        description: 'Most people per firm; default 25. Each person new to the firm is charged.',
      },
    },
    required: ['firms'],
    additionalProperties: false,
  },
};

const SEARCH_COMPANIES: ToolDescriptor = {
  name: 'search_companies',
  description:
    "Find firms by name, domain or industry in Parcel's company index and its data provider. Returns each firm's " +
    'Parcel account id when Parcel knows it, domain, LinkedIn URL, industry and headcount. Charged per company ' +
    "returned from the workspace's enrichment credits, cached results included; nothing when none is found.",
  inputSchema: {
    type: 'object',
    properties: {
      names: {
        type: 'array',
        minItems: 1,
        maxItems: 50,
        items: { type: 'string', minLength: 1 },
        description: 'Firm names.',
      },
      domains: {
        type: 'array',
        minItems: 1,
        maxItems: 50,
        items: { type: 'string', minLength: 1 },
        description: 'Firm web domains.',
      },
      industries: {
        type: 'array',
        minItems: 1,
        maxItems: 10,
        items: { type: 'string', minLength: 1 },
        description: 'Industries.',
      },
      countries: {
        type: 'array',
        minItems: 1,
        maxItems: 10,
        items: { type: 'string', minLength: 2, maxLength: 2 },
        description: 'Two-letter country codes; default US.',
      },
      limit: {
        type: 'integer',
        minimum: 1,
        maximum: 100,
        description: 'Most companies; default 25. Each company returned is charged.',
      },
    },
    anyOf: [{ required: ['names'] }, { required: ['domains'] }, { required: ['industries'] }],
    additionalProperties: false,
  },
};

const REVEAL_CONTACT: ToolDescriptor = {
  name: 'reveal_contact',
  description:
    "Reveal a Parcel contact's email or full profile (employment history, education, skills) for this workspace. " +
    "Charged only when found, from the workspace's enrichment credits; free when this workspace already revealed it. " +
    'Revealing more than one? Pass ids (up to 100) in a SINGLE call; results return per contact, in input order, and ' +
    'one failure never blocks the rest. Phone numbers are not offered yet.',
  inputSchema: {
    type: 'object',
    properties: {
      id: { type: 'string', format: 'uuid', description: 'The Parcel contact id.' },
      ids: {
        type: 'array',
        items: { type: 'string', format: 'uuid' },
        minItems: 1,
        maxItems: BATCH_MAX,
        description: 'Batch mode: 1-100 contact ids. Use INSTEAD of id.',
      },
      kinds: {
        type: 'array',
        items: { type: 'string', enum: ['email', 'profile'] },
        minItems: 1,
        maxItems: 2,
        description: 'What to reveal: email, profile, or both.',
      },
    },
    required: ['kinds'],
    oneOf: [{ required: ['id'] }, { required: ['ids'] }],
    additionalProperties: false,
  },
};

const ENRICH_FROM_LINKEDIN: ToolDescriptor = {
  name: 'enrich_from_linkedin',
  description:
    'Enrich from a LinkedIn URL. A person URL (/in/...) fetches the full profile, adds the person to Parcel and ' +
    "reveals the profile for this workspace; a company URL (/company/...) fills the firm's profile. Each is charged " +
    "from the workspace's enrichment credits only when found. More than one URL? Pass urls (up to 100) in a SINGLE " +
    'call; results return per URL, in input order.',
  inputSchema: withBatchInputs({
    properties: {
      url: { type: 'string', minLength: 1, description: 'A LinkedIn person or company URL.' },
    },
    singleConstraint: { required: ['url'] },
    itemSchema: { type: 'string', minLength: 1 },
    batchKey: 'urls',
    batchDescription: 'Batch mode: 1-100 LinkedIn URLs. Use INSTEAD of url.',
  }),
};

// ---------------------------------------------------------------------------
// Scoring: one read tool and one write tool, so a read-only connection stays simple
// ---------------------------------------------------------------------------

export const SCORE_KEYS = ['icp', 'atlas', 'prospect'] as const;
export const SCORE_READ_ACTIONS = ['list', 'get', 'preview'] as const;
export const SCORE_WRITE_ACTIONS = ['update', 'seed'] as const;

const SCORES: ToolDescriptor = {
  name: 'workspace_scores',
  description:
    'ICP Score (accounts, key icp), Atlas Score (projects, atlas), Prospect Score (contacts, ' +
    "prospect). is_template only marks these built-in three; their criteria are the workspace's own. " +
    "list; get {key}; preview {key, criteria} counts buckets and each criterion's matches, unsaved. " +
    "Spec's approval card shows the same counts, so Spec need not preview before proposing. " +
    'Buckets are score bands 75/50/25; the last also holds disqualified ' +
    'records. A score ranks every record the workspace can see, not only saved ones. Explain ' +
    'scores.<key>.bucket from matched. Read-only. Pro. 0 credits.',
  inputSchema: {
    type: 'object',
    properties: {
      action: { type: 'string', enum: [...SCORE_READ_ACTIONS] },
      key: { type: 'string', enum: [...SCORE_KEYS] },
      criteria: { type: 'array', items: { type: 'object' }, maxItems: 40 },
    },
    required: ['action'],
    additionalProperties: false,
  },
};

const MANAGE_SCORES: ToolDescriptor = {
  name: 'workspace_manage_scores',
  description:
    'Change scoring. update replaces one score (key, bucket_labels[4] top first and kept unless ' +
    'the member asks to rename them, criteria[]) and ' +
    'recomputes it and the scores after it; seed fills all three from onboarding answers. A ' +
    'criterion is {id, label?, impact, where}: where is a condition {combinator, rules:[{field, ' +
    "operator, value}]} on the score's records (fields: search_fields dataset " +
    'workspace_accounts|projects|contacts). Rules in one group on one prefix (firm., project., ' +
    'contact., signal.) match the same linked record. A score reads only an earlier score: ' +
    'firm.score between [75,100] is ICP Tier 1. impact: very_important, important, ' +
    'somewhat_important, not_important, disqualify. Outside Spec, check with workspace_scores preview first. ' +
    'Owners and admins. Requires Pro. 0 credits.',
  inputSchema: {
    type: 'object',
    properties: {
      action: { type: 'string', enum: [...SCORE_WRITE_ACTIONS] },
      key: { type: 'string', enum: [...SCORE_KEYS], description: 'Score key (update).' },
      expected_version: {
        type: 'integer',
        minimum: 1,
        description: 'update: the version you read; a newer one is refused.',
      },
      bucket_labels: {
        type: 'array',
        items: { type: 'string', minLength: 1, maxLength: 30 },
        minItems: 4,
        maxItems: 4,
        description: 'Four labels, top first (update).',
      },
      criteria: {
        type: 'array',
        maxItems: 40,
        items: {
          type: 'object',
          required: ['id', 'impact', 'where'],
          properties: {
            id: { type: 'string' },
            label: { type: 'string', maxLength: 80 },
            impact: { enum: [...SCORE_IMPACTS] },
            where: { type: 'object' },
          },
        },
      },
      answers: {
        type: 'object',
        description:
          'seed: markets[] (cities), sectors[] (primary uses), stages[], stakeholders[] (account types), size_floor? {kind: units|sqft|cost, min}.',
      },
    },
    required: ['action'],
    additionalProperties: false,
  },
};

// ---------------------------------------------------------------------------
// Advertised order. Changing it changes tools/list output, so it is fixed here
// and asserted by the MCP parity test.
// ---------------------------------------------------------------------------

export const NATIVE_TOOL_DESCRIPTORS: readonly ToolDescriptor[] = [
  ...buildDataDescriptors(),
  SEARCH_FIELDS,
  GET_FIELDS,
  ...buildWorkspaceReadDescriptors(),
  SEARCH_VIEWS,
  MANAGE_VIEWS,
  FOLLOW,
  FEED,
  SCORES,
  MANAGE_SCORES,
  FIND_KNOWLEDGE,
  GET_KNOWLEDGE,
  MANAGE_KNOWLEDGE,
  UPSERT_ACCOUNT,
  DELETE_ACCOUNT,
  UPSERT_PROJECT,
  DELETE_PROJECT,
  UPSERT_CONTACT,
  DELETE_CONTACT,
  LIST_SUBSCRIPTIONS,
  UPSERT_SUBSCRIPTION,
  DELETE_SUBSCRIPTION,
  LIST_MEMBERS,
  UPDATE_MEMBER,
  DELETE_MEMBER,
  CREATE_INVITATION,
  LIST_INVITATIONS,
  DELETE_INVITATION,
  GET_INFO,
  GET_CREDITS,
  SUBMIT_DATA_FEEDBACK,
  QUERY_DATA,
  WEB_SEARCH,
  SEARCH_PEOPLE,
  SEARCH_COMPANIES,
  REVEAL_CONTACT,
  ENRICH_FROM_LINKEDIN,
] as const;
